<div class="admin-livekit-container">
  <div class="page-header">
    <div>
      <h1 i18n="@@adminLivekit.title">LiveKit</h1>
      <p class="subtitle" i18n="@@adminLivekit.subtitle">
        Raw view of LiveKit SIP trunks and dispatch rules. Direct passthrough to the cluster.
      </p>
    </div>
    <div class="header-actions">
      <p-button
        icon="pi pi-sync"
        severity="secondary"
        [outlined]="true"
        [loading]="isProvisioningTrunks()"
        (click)="askProvisionSystemTrunks()"
        i18n-label="@@adminLivekit.provisionSystemTrunks"
        label="Provision system trunks"
        i18n-pTooltip="@@adminLivekit.provisionSystemTrunks.tooltip"
        pTooltip="Ensure the built-in system SIP trunks and their dispatch rules exist on the LiveKit cluster (upsert — safe to run repeatedly)."
        tooltipPosition="top">
      </p-button>
      <p-button
        icon="pi pi-refresh"
        severity="secondary"
        [outlined]="true"
        [loading]="isLoadingTrunks() || isLoadingRules()"
        (click)="refresh()"
        i18n-pTooltip="@@common.refresh"
        pTooltip="Refresh"
        tooltipPosition="top"
        i18n-ariaLabel="@@common.refresh"
        ariaLabel="Refresh">
      </p-button>
    </div>
  </div>

  <p-tabs value="dispatchRules">
    <p-tablist>
      <p-tab value="dispatchRules" i18n="@@adminLivekit.tab.dispatchRules">Dispatch rules</p-tab>
      <p-tab value="trunks" i18n="@@adminLivekit.tab.trunks">Trunks</p-tab>
    </p-tablist>

    <p-tabpanels>
      <!-- Dispatch rules tab -->
      <p-tabpanel value="dispatchRules">
        @if (isLoadingRules()) {
          <div class="loading-container">
            <p-progressSpinner />
          </div>
        } @else {
          <p-table [value]="dispatchRules()" styleClass="lk-table" [tableStyle]="{ 'min-width': '50rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th i18n="@@adminLivekit.col.id">ID</th>
                <th i18n="@@adminLivekit.col.name">Name</th>
                <th class="hide-mobile" i18n="@@adminLivekit.col.roomPrefix">Room prefix</th>
                <th class="hide-mobile" i18n="@@adminLivekit.col.roomPreset">Room preset</th>
                <th i18n="@@adminLivekit.col.trunkIds">Trunks</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rule>
              <tr>
                <td><code class="cell-mono">{{ rule.dispatch_rule_id }}</code></td>
                <td>{{ rule.name || '—' }}</td>
                <td class="hide-mobile">{{ rule.room_prefix || '—' }}</td>
                <td class="hide-mobile">{{ rule.room_preset || '—' }}</td>
                <td>{{ rule.trunk_ids?.length || 0 }}</td>
                <td>
                  <div class="action-buttons">
                    <p-button
                      icon="pi pi-eye"
                      [text]="true"
                      size="small"
                      severity="secondary"
                      i18n-pTooltip="@@adminLivekit.viewDetails"
                      pTooltip="View details"
                      tooltipPosition="top"
                      (click)="openRule(rule)"
                      i18n-ariaLabel="@@adminLivekit.viewDetails"
                      ariaLabel="View details">
                    </p-button>
                    <p-button
                      icon="pi pi-trash"
                      [text]="true"
                      size="small"
                      severity="danger"
                      i18n-pTooltip="@@common.delete"
                      pTooltip="Delete"
                      tooltipPosition="top"
                      (click)="askDeleteRule(rule)"
                      i18n-ariaLabel="@@common.delete"
                      ariaLabel="Delete">
                    </p-button>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="empty-message" i18n="@@adminLivekit.empty.rules">
                  No dispatch rules.
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </p-tabpanel>

      <!-- Trunks tab -->
      <p-tabpanel value="trunks">
        @if (isLoadingTrunks()) {
          <div class="loading-container">
            <p-progressSpinner />
          </div>
        } @else {
          <div class="trunks-toolbar">
            <p-iconfield iconPosition="left">
              <p-inputicon styleClass="pi pi-search" />
              <input
                pInputText
                type="text"
                [ngModel]="trunkSearchQuery()"
                (ngModelChange)="onTrunkSearchInput($event)"
                i18n-placeholder="@@adminLivekit.search.byId"
                placeholder="Search by trunk ID..." />
            </p-iconfield>
          </div>

          <div class="trunk-group">
            <h2 class="group-title" i18n="@@adminLivekit.inboundTrunks">Inbound trunks</h2>
            <p-table [value]="filteredInboundTrunks()" styleClass="lk-table" [tableStyle]="{ 'min-width': '50rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th i18n="@@adminLivekit.col.id">ID</th>
                  <th i18n="@@adminLivekit.col.name">Name</th>
                  <th i18n="@@adminLivekit.col.address">Address</th>
                  <th i18n="@@adminLivekit.col.numbersCount">Numbers</th>
                  <th i18n="@@adminLivekit.col.country">Country</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-trunk>
                <tr>
                  <td><code class="cell-mono">{{ trunk.trunk_id }}</code></td>
                  <td>{{ trunk.name || '—' }}</td>
                  <td>{{ trunk.address || '—' }}</td>
                  <td>{{ trunk.numbers?.length || 0 }}</td>
                  <td>{{ trunk.destination_country || '—' }}</td>
                  <td>
                    <div class="action-buttons">
                      <p-button
                        icon="pi pi-eye"
                        [text]="true"
                        size="small"
                        severity="secondary"
                        i18n-pTooltip="@@adminLivekit.viewDetails"
                        pTooltip="View details"
                        tooltipPosition="top"
                        (click)="openTrunk(trunk)"
                        i18n-ariaLabel="@@adminLivekit.viewDetails"
                        ariaLabel="View details">
                      </p-button>
                      <p-button
                        icon="pi pi-trash"
                        [text]="true"
                        size="small"
                        severity="danger"
                        i18n-pTooltip="@@common.delete"
                        pTooltip="Delete"
                        tooltipPosition="top"
                        (click)="askDeleteTrunk(trunk)"
                        i18n-ariaLabel="@@common.delete"
                        ariaLabel="Delete">
                      </p-button>
                    </div>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="6" class="empty-message" i18n="@@adminLivekit.empty.inbound">
                    No inbound trunks.
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <div class="trunk-group">
            <h2 class="group-title" i18n="@@adminLivekit.outboundTrunks">Outbound trunks</h2>
            <p-table [value]="filteredOutboundTrunks()" styleClass="lk-table" [tableStyle]="{ 'min-width': '50rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th i18n="@@adminLivekit.col.id">ID</th>
                  <th i18n="@@adminLivekit.col.name">Name</th>
                  <th i18n="@@adminLivekit.col.address">Address</th>
                  <th i18n="@@adminLivekit.col.transport">Transport</th>
                  <th i18n="@@adminLivekit.col.country">Country</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-trunk>
                <tr>
                  <td><code class="cell-mono">{{ trunk.trunk_id }}</code></td>
                  <td>{{ trunk.name || '—' }}</td>
                  <td>{{ trunk.address || '—' }}</td>
                  <td>{{ trunk.transport || '—' }}</td>
                  <td>{{ trunk.destination_country || '—' }}</td>
                  <td>
                    <div class="action-buttons">
                      <p-button
                        icon="pi pi-eye"
                        [text]="true"
                        size="small"
                        severity="secondary"
                        i18n-pTooltip="@@adminLivekit.viewDetails"
                        pTooltip="View details"
                        tooltipPosition="top"
                        (click)="openTrunk(trunk)"
                        i18n-ariaLabel="@@adminLivekit.viewDetails"
                        ariaLabel="View details">
                      </p-button>
                      <p-button
                        icon="pi pi-trash"
                        [text]="true"
                        size="small"
                        severity="danger"
                        i18n-pTooltip="@@common.delete"
                        pTooltip="Delete"
                        tooltipPosition="top"
                        (click)="askDeleteTrunk(trunk)"
                        i18n-ariaLabel="@@common.delete"
                        ariaLabel="Delete">
                      </p-button>
                    </div>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="6" class="empty-message" i18n="@@adminLivekit.empty.outbound">
                    No outbound trunks.
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        }
      </p-tabpanel>
    </p-tabpanels>
  </p-tabs>
</div>

<!-- Trunk details drawer -->
<p-drawer
  [visible]="trunkDrawerVisible()"
  (visibleChange)="trunkDrawerVisible.set($event)"
  position="right"
  [style]="{ width: '520px' }"
  [modal]="true"
  (onHide)="closeTrunkDrawer()">
  <ng-template pTemplate="header">
    <h2 class="drawer-title" i18n="@@adminLivekit.trunk.detailsTitle">Trunk details</h2>
  </ng-template>
  @if (selectedTrunk(); as trunk) {
    <div class="drawer-content">
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.id">ID</span>
        <code class="kv-value">{{ trunk.trunk_id }}</code>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.name">Name</span>
        <span class="kv-value">{{ trunk.name || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.address">Address</span>
        <code class="kv-value">{{ trunk.address || '—' }}</code>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.transport">Transport</span>
        <span class="kv-value">{{ trunk.transport || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.country">Destination country</span>
        <span class="kv-value">{{ trunk.destination_country || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.authUsername">Auth username</span>
        <span class="kv-value">{{ trunk.auth_username || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.numbers">Numbers</span>
        @if ((trunk.numbers?.length ?? 0) > 0) {
          <div class="kv-list">
            @for (n of trunk.numbers; track n) {
              <code class="kv-chip">{{ n }}</code>
            }
          </div>
        } @else {
          <span class="kv-value">—</span>
        }
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.allowedAddresses">Allowed addresses</span>
        @if ((trunk.allowed_addresses?.length ?? 0) > 0) {
          <div class="kv-list">
            @for (a of trunk.allowed_addresses; track a) {
              <code class="kv-chip">{{ a }}</code>
            }
          </div>
        } @else {
          <span class="kv-value">—</span>
        }
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.headersToAttributes">Headers → Attributes</span>
        @if ((trunk.headers_to_attributes?.length ?? 0) > 0) {
          <div class="kv-list">
            @for (m of trunk.headers_to_attributes; track m.header + '→' + m.attribute) {
              <div class="kv-chip-row">
                <code class="kv-chip">{{ m.header || '?' }} → {{ m.attribute || '?' }}</code>
              </div>
            }
          </div>
        } @else {
          <span class="kv-value">—</span>
        }
      </div>
    </div>
  }
</p-drawer>

<!-- Dispatch rule details drawer -->
<p-drawer
  [visible]="ruleDrawerVisible()"
  (visibleChange)="ruleDrawerVisible.set($event)"
  position="right"
  [style]="{ width: '520px' }"
  [modal]="true"
  (onHide)="closeRuleDrawer()">
  <ng-template pTemplate="header">
    <h2 class="drawer-title" i18n="@@adminLivekit.rule.detailsTitle">Dispatch rule details</h2>
  </ng-template>
  @if (selectedRule(); as rule) {
    <div class="drawer-content">
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.id">ID</span>
        <code class="kv-value">{{ rule.dispatch_rule_id }}</code>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.name">Name</span>
        <span class="kv-value">{{ rule.name || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.kind">Kind</span>
        <span class="kv-value">{{ rule.kind || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.roomPrefix">Room prefix</span>
        <code class="kv-value">{{ rule.room_prefix || '—' }}</code>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.roomPreset">Room preset</span>
        <span class="kv-value">{{ rule.room_preset || '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.roomName">Room name</span>
        <code class="kv-value">{{ rule.room_name || '—' }}</code>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.priority">Priority</span>
        <span class="kv-value">{{ rule.priority ?? '—' }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.randomize">Randomize</span>
        <span class="kv-value">{{ rule.randomize === true ? 'true' : (rule.randomize === false ? 'false' : '—') }}</span>
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.numbers">Numbers</span>
        @if ((rule.numbers?.length ?? 0) > 0) {
          <div class="kv-list">
            @for (n of rule.numbers; track n) {
              <code class="kv-chip">{{ n }}</code>
            }
          </div>
        } @else {
          <span class="kv-value">—</span>
        }
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.field.inboundNumbers">Inbound numbers</span>
        @if ((rule.inbound_numbers?.length ?? 0) > 0) {
          <div class="kv-list">
            @for (n of rule.inbound_numbers; track n) {
              <code class="kv-chip">{{ n }}</code>
            }
          </div>
        } @else {
          <span class="kv-value">—</span>
        }
      </div>
      <div class="kv">
        <span class="kv-key" i18n="@@adminLivekit.col.trunkIds">Trunk IDs</span>
        @if ((rule.trunk_ids?.length ?? 0) > 0) {
          <div class="kv-list">
            @for (tid of rule.trunk_ids; track tid) {
              <div class="kv-chip-row">
                <code class="kv-chip">{{ tid }}</code>
                <p-button
                  icon="pi pi-copy"
                  [text]="true"
                  size="small"
                  severity="secondary"
                  i18n-pTooltip="@@common.copy"
                  pTooltip="Copy"
                  tooltipPosition="top"
                  (click)="copyText(tid)"
                  i18n-ariaLabel="@@common.copy"
                  ariaLabel="Copy">
                </p-button>
              </div>
            }
          </div>
        } @else {
          <span class="kv-value">—</span>
        }
      </div>
    </div>
  }
</p-drawer>

<!-- Delete confirmation (shared) -->
<app-delete-confirmation
  [visible]="deleteDialogVisible()"
  (visibleChange)="deleteDialogVisible.set($event)"
  [title]="deleteTitle"
  [subtitle]="deleteSubtitle"
  [deleting]="isDeleting()"
  (confirm)="confirmDelete()"
  (cancel)="cancelDelete()" />
